import { AbilityBuilder, createMongoAbility } from "@casl/ability";
import { parsePermission } from "./permissions.js";
/**
 * CASL action and subject sets, narrowed from the permission catalogue.
 *
 * Subjects: `'Workspace' | 'Member' | 'Invitation' | 'ApiKey' | 'Agent' | 'KbDocument' | 'Run' | 'HandoffEvent' | 'all'`
 * Actions: `'read' | 'update' | 'create' | 'invite' | 'update-role' | 'remove' | 'revoke' | 'delete' | 'upload' | 'sync' | 'attach' | 'take-over' | 'manage'`
 *
 * Subject names are PascalCase (CASL convention); permission slugs use
 * kebab-case (e.g., `member:update-role`). The mapping below converts.
 */
export const SUBJECTS = [
    "Workspace",
    "Member",
    "Invitation",
    "ApiKey",
    "Agent",
    "KbDocument",
    "Run",
    "HandoffEvent",
    "Decision",
    // Customer conversations — `monitor` (live queue/board) + `read` (open a
    // transcript). Renamed from `Session` (which read as an infra/session
    // concept, not "a customer chat").
    "Conversation",
    "DataTable",
    "Feedback",
    // Workflows + stored queries (rbac-permissions PR 1) — un-borrowed from
    // the Agent / DataTable subjects they used to ride on.
    "Workflow",
    "StoredQuery",
    // Helpdesk team delegation (helpdesk-team unit): scoped member/role
    // management for the desk. Distinct from HandoffEvent (queue operations).
    "Helpdesk",
    // Widget signing keys (contacts unit) — permission-gated admin surface.
    "SigningKey",
    // Org-tier subjects (B1 — RBAC depth). Additive; existing rules unchanged.
    "Organization",
    "OrgMember",
    "Billing",
    "Platform",
    "all",
];
/**
 * Maps permission-slug subject part (`member`, `api-key`, `kb`, ...) to
 * the CASL `Subject` name (`Member`, `ApiKey`, `KbDocument`, ...). Names
 * differ because subjects are conventionally PascalCase singular while
 * slugs are kebab-case.
 */
const SUBJECT_MAP = {
    workspace: "Workspace",
    member: "Member",
    invitation: "Invitation",
    "api-key": "ApiKey",
    agent: "Agent",
    // Both `kb:*` and `knowledge:*` permission slugs map to the same CASL
    // subject (`KbDocument`). The `knowledge` alias was added by KB unit
    // PR-A.2 so newer call sites can read more naturally; the original
    // `kb` slug remains the canonical form on the wire.
    kb: "KbDocument",
    knowledge: "KbDocument",
    run: "Run",
    handoff: "HandoffEvent",
    // `decision:rule` and `conversation:*` (monitor / read) are
    // the M9 human-handoff additions. `handoff:*` already maps to `HandoffEvent`.
    decision: "Decision",
    conversation: "Conversation",
    "data-table": "DataTable",
    feedback: "Feedback",
    // Workflows + stored queries (rbac-permissions PR 1).
    workflow: "Workflow",
    query: "StoredQuery",
    // Helpdesk team delegation. `handoff:*` (queue ops) stays on HandoffEvent;
    // `helpdesk:*` is the desk's own member/role management surface.
    helpdesk: "Helpdesk",
    // Widget signing keys (contacts unit).
    "signing-key": "SigningKey",
    // Org-tier slugs (B1). `workspace:create` reuses the existing `Workspace`
    // subject, so no mapping entry is needed for it here.
    org: "Organization",
    "org-member": "OrgMember",
    billing: "Billing",
    platform: "Platform",
};
function permissionToRule(perm) {
    const { subject: subjectSlug, action } = parsePermission(perm);
    const subject = SUBJECT_MAP[subjectSlug];
    // Unmapped slug: a permission retired in code but still carried by an
    // already-minted JWT or a stored RoleDefinition (its `permissions` column is
    // deliberately `text[]` "so a slug retired in code doesn't break reads").
    // Skip it — fail closed on that single grant — rather than throw and 500
    // every request the stale principal makes. Same graceful-degradation stance
    // as the workspace guard's unknown-role branch. Fresh logins re-derive
    // permissions from the current bundles, so the stale slug is transient.
    if (!subject)
        return null;
    return { action: action, subject };
}
/**
 * Build a CASL ability for the given principal in the given workspace.
 *
 * Fresh per request (cheap — 22 permissions, < 1ms). No cache —
 * cache-invalidation bugs on role change are not worth the savings.
 *
 * Per NFR Design ND9.
 *
 * The `workspaceId` is part of the ability's CONTEXT for future
 * instance-level checks (e.g., `ability.can('update', subject)` on a
 * specific document), but in M3 we only check action+subject pairs.
 *
 * @param principal the resolved principal from the request
 * @param _ctx contextual scope (currently just workspaceId; reserved for
 *   future instance checks)
 */
export function defineAbilityFor(principal, _ctx) {
    const { can, build } = new AbilityBuilder(createMongoAbility);
    // Platform staff (CometChat internal / FDE) are cross-tenant superusers:
    // full access regardless of their org or workspace role, so support can see
    // and operate every surface — feature flags, roles, members, etc. — in any
    // workspace they're in. Gated externally by the `PLATFORM_STAFF_EMAILS`
    // allow-list, so it can't be self-granted.
    if (principal.permissions.includes("platform:staff")) {
        can("manage", "all");
        return build();
    }
    for (const perm of principal.permissions) {
        const rule = permissionToRule(perm);
        if (rule)
            can(rule.action, rule.subject);
    }
    return build();
}
//# sourceMappingURL=ability.js.map